import { Badge, Checkbox, Stack, Table, Text } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type {
  GateCheckId,
  MergeGatePredicate,
  MergeGateResponse,
  PredicateClass,
  PredicateOutcome,
} from "./api";
import { isAcknowledged, predicatesByClass } from "./logic";
import type { AcknowledgedTokens } from "./logic";
import { predicateLabel } from "./refusalText";

const CLASS_ORDER: readonly PredicateClass[] = [
  "prohibiting",
  "acceptable",
  "not_participating",
];

const OUTCOME_COLOR: Readonly<Record<PredicateOutcome, string>> = {
  passed: "green",
  failed: "red",
  unknown: "gray",
};

// Не пройденный предикат класса not_participating слиянию не мешает,
// поэтому красным не подсвечивается.
function outcomeColor(predicate: MergeGatePredicate): string {
  return predicate.predicate_class === "not_participating" &&
    predicate.outcome === "failed"
    ? "gray"
    : OUTCOME_COLOR[predicate.outcome];
}

interface MergeGatePredicatesProps {
  readonly gate: MergeGateResponse;
  readonly acknowledged: AcknowledgedTokens;
  readonly onAcknowledgeChange: (
    id: GateCheckId,
    stateToken: string,
    checked: boolean,
  ) => void;
  readonly disabled: boolean;
}

// Предикаты гейта по классам: запрещающие, принимаемые, не участвующие.
// Под принимаемым ложным предикатом — чекбокс «принимаю на себя»,
// привязанный к токену состояния, который человек видит сейчас.
export function MergeGatePredicates({
  gate,
  acknowledged,
  onAcknowledgeChange,
  disabled,
}: MergeGatePredicatesProps): ReactElement {
  const { t } = useTranslation();
  const groups = predicatesByClass(gate);
  return (
    <Stack gap="md">
      {CLASS_ORDER.filter((cls) => groups[cls].length > 0).map((cls) => (
        <Stack key={cls} gap="xs">
          <Text fw={600}>{t(`mergeApproval.class.${cls}`)}</Text>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("mergeApproval.colPredicate")}</Table.Th>
                <Table.Th>{t("mergeApproval.colOutcome")}</Table.Th>
                <Table.Th>{t("mergeApproval.colReason")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {groups[cls].map((predicate) => (
                <Table.Tr key={predicate.id}>
                  <Table.Td>{predicateLabel(predicate.id, t)}</Table.Td>
                  <Table.Td>
                    <Badge color={outcomeColor(predicate)}>
                      {t(`mergeApproval.outcome.${predicate.outcome}`)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{predicate.reason}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {groups[cls]
            .flatMap((p) =>
              p.predicate_class === "acceptable" &&
              p.outcome === "failed" &&
              p.state_token !== null
                ? [{ predicate: p, stateToken: p.state_token }]
                : [],
            )
            .map(({ predicate, stateToken }) => (
              <Checkbox
                key={predicate.id}
                label={t("mergeApproval.acknowledgeLabel", {
                  predicate: predicateLabel(predicate.id, t),
                })}
                checked={isAcknowledged(predicate, acknowledged)}
                disabled={disabled}
                onChange={(e) => {
                  onAcknowledgeChange(
                    predicate.id,
                    stateToken,
                    e.currentTarget.checked,
                  );
                }}
              />
            ))}
        </Stack>
      ))}
    </Stack>
  );
}
